import { identity, isFunction, uniqueId } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

export const BUSY_DELAY = 150;

export function useDataLoader(props) {
    const { id, promises = [], skipSpinner, onSuccess, onError } = props,
        [ready, setReady] = useState(!promises.length),
        [error, setError] = useState(false),
        [active, setActive] = useState(ready),
        currentState = useMemo(() => app.store.getState(), [promises]), // gets the full state
        [instanceId] = useState(uniqueId(`${id}-`)),
        dispatch = useDispatch();

    let showSpinner;
    try {
        showSpinner = !ready || !(skipSpinner && (!isFunction(skipSpinner) || skipSpinner(currentState)));
    } catch (error) {
        showSpinner = !ready;
    }

    useEffect(() => {
        if (id) {
            // NOTE: to improve performance, only identified loaders are registered on the store

            const addLoader = () => dispatch(app.actions.ui.activeLoaders.start(instanceId)),
                removeLoader = () => dispatch(app.actions.ui.activeLoaders.stop(instanceId));

            let timeout = setTimeout(addLoader, 100);
            Promise.all(promises.filter(identity))
                .then(() => clearTimeout(timeout))
                .then(removeLoader);

            return removeLoader;
        }
    }, promises);

    useEffect(() => {
        let isWaitingForCompletion = true,
            isBusy = false;

        // body.busy covers the app with a click-swallowing spinner overlay, so short
        // loads must never raise it
        const busyTimeout = showSpinner
            ? setTimeout(() => {
                  isBusy = true;
                  dispatch(app.actions.ui.busy());
              }, BUSY_DELAY)
            : null;

        const free = () => {
            clearTimeout(busyTimeout);
            if (isBusy) {
                isBusy = false;
                dispatch(app.actions.ui.free());
            }
        };

        Promise.all(promises.filter(identity))
            .then(() => isWaitingForCompletion && setReady(true))
            .catch((error) => {
                if (isWaitingForCompletion) {
                    setError(error);
                }
            })
            .then(free);
        setActive(true);

        return () => {
            isWaitingForCompletion = false;
            free();
        };
    }, promises);

    useEffect(() => {
        if (ready && onSuccess) onSuccess();
        else if (error && onError) onError(error);
    }, [ready, error]);

    return { ready, error, active, showSpinner };
}
